import { MEDIA_TYPE_LABELS, type MediaType } from '@deizi/shared';
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon } from '../../components/Icon';
import { Combobox } from '../../components/Combobox';
import { productOption, productVariantOption } from '../../components/pickers';
import { Sheet } from '../../components/Overlays';
import { useToast } from '../../components/Toast';
import { MEDIA_ACCEPT, useInvalidateMedia } from '../../lib/media-api';
import { useProduct, useProducts } from '../../lib/products-api';
import { type QueuedMedia, statusText, useMediaQueue } from './upload-queue';

/** The type chips per file (one type per item): photos and videos get their own short lists. */
const CHIPS: Record<'photo' | 'video', MediaType[]> = {
  photo: ['product', 'lifestyle', 'detail', 'packaging', 'customer'],
  video: ['timelapse', 'making_of', 'product'],
};

const isVideo = (i: QueuedMedia) =>
  i.file.type.startsWith('video/') || /\.(mp4|mov|m4v)$/i.test(i.file.name);

/** Phone "Enviar mídia" `/midia/enviar` (board Midia-Celular). */
export function MediaUploadPage() {
  const [params] = useSearchParams();
  const toast = useToast();
  const refresh = useInvalidateMedia();
  const products = useProducts({ segment: 'all', category: '', material: '' });
  const [productId, setProductId] = useState(params.get('produto') ?? '');
  const [variantId, setVariantId] = useState('');
  const product = useProduct(productId || undefined);
  const queue = useMediaQueue(
    productId ? { productId, variantId: variantId || null } : null,
    refresh,
  );
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const [removing, setRemoving] = useState<QueuedMedia | null>(null);
  const [done, setDone] = useState(false);

  const n = queue.items.length;
  const sentCount = queue.items.filter((i) => i.status === 'added').length;
  const pending = queue.active;
  const label = product.data
    ? `${product.data.name}${variantId ? ` · ${product.data.variants.find((v) => v.id === variantId)?.name ?? ''}` : ''}`
    : '';

  const pick = (files: FileList | null) => {
    if (!productId) {
      toast('Escolha o produto primeiro');
      return;
    }
    queue.add(files, 'product');
    setDone(false);
  };

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8 }}>
        <Link className="btn icon g" to="/mais" aria-label="Voltar">
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
            <Icon name="chevronRight" />
          </span>
        </Link>
        <h1 style={{ fontSize: 22 }} className="grow">
          Enviar mídia
        </h1>
      </div>

      {done ? (
        <div
          className="card col"
          style={{ gap: 10, alignItems: 'flex-start', borderColor: 'var(--ok)' }}
        >
          <span className="badge ok">
            <Icon name="check" size={14} />
            Salvo
          </span>
          <h2 style={{ fontSize: 18 }}>Mídia salva em {label}</h2>
          <p className="sm muted">
            {pending
              ? `${pending} ${pending > 1 ? 'arquivos continuam' : 'arquivo continua'} enviando. Deixe esta tela aberta até terminar; se a conexão cair, o envio continua de onde parou.`
              : 'Tudo enviado. As fotos já aparecem na aba Mídia do produto.'}
          </p>
          <Link className="btn p block" to={`/produtos/${productId}/midia`}>
            Ver mídia de {product.data?.name ?? 'produto'}
          </Link>
          <button type="button" className="btn block" onClick={() => setDone(false)}>
            Enviar mais
          </button>
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="mu-p">Produto</label>
        <Combobox
          id="mu-p"
          value={productId}
          placeholder="Escolha o produto"
          options={(products.data?.data ?? []).map(productOption)}
          onChange={(v) => {
            setProductId(v);
            setVariantId('');
          }}
        />
      </div>
      {product.data && product.data.variants.length > 0 ? (
        <div className="field">
          <label htmlFor="mu-v">Variação</label>
          <Combobox
            id="mu-v"
            value={variantId}
            options={[
              { value: '', label: 'Todas (produto)', pinned: 'top' },
              ...product.data.variants.map(productVariantOption),
            ]}
            onChange={setVariantId}
          />
          <span className="help">Vale para os próximos arquivos que você escolher.</span>
        </div>
      ) : null}

      <input
        ref={camera}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        hidden
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={gallery}
        type="file"
        accept={MEDIA_ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button
          type="button"
          className="btn"
          disabled={!productId}
          onClick={() => camera.current?.click()}
        >
          <Icon name="image" />
          Câmera
        </button>
        <button
          type="button"
          className="btn"
          disabled={!productId}
          onClick={() => gallery.current?.click()}
        >
          <Icon name="plus" />
          Galeria
        </button>
      </div>

      <div className="row between">
        <h2 style={{ fontSize: 18 }}>
          {n} {n === 1 ? 'arquivo' : 'arquivos'}
        </h2>
        <span className="sm muted tab" aria-live="polite">
          {sentCount} de {n} enviados
        </span>
      </div>

      {n > 0 ? (
        <div className="card col" style={{ gap: 0, padding: '4px 12px' }}>
          {queue.items.map((i) => {
            const pct = i.total ? Math.round((i.sent / i.total) * 100) : 0;
            const up = i.status === 'uploading' || i.status === 'offline';
            const video = isVideo(i);
            return (
              <div
                key={i.key}
                className="row"
                style={{
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: '1px solid var(--line)',
                  alignItems: 'flex-start',
                }}
              >
                {i.preview ? (
                  <img
                    src={i.preview}
                    alt=""
                    style={{
                      width: 52,
                      height: 52,
                      flex: '0 0 52px',
                      objectFit: 'cover',
                      borderRadius: 12,
                    }}
                  />
                ) : (
                  <div
                    className="ph"
                    style={{ width: 52, height: 52, flex: '0 0 52px', padding: 2 }}
                  >
                    {video ? 'vídeo' : 'foto'}
                  </div>
                )}
                <div className="grow col" style={{ gap: 8, minWidth: 0 }}>
                  <div className="row between sm" style={{ gap: 6 }}>
                    <span
                      className="trunc"
                      style={{ fontWeight: 500, flex: '1 1 auto' }}
                      title={i.file.name}
                    >
                      {i.file.name}
                    </span>
                    {i.status === 'added' ? (
                      <span className="badge ok">Enviado</span>
                    ) : i.status === 'paused' ? (
                      <span className="badge warn">Pausado</span>
                    ) : i.status === 'error' ? (
                      <span className="badge err">Falhou</span>
                    ) : null}
                  </div>
                  {up ? (
                    <div className="layers thin">
                      <div className="fill" style={{ width: `${pct}%` }} />
                      <div className="nozzle" style={{ left: `${pct}%` }} />
                    </div>
                  ) : null}
                  {i.status !== 'added' ? (
                    <p
                      className="xs"
                      style={{
                        color:
                          i.status === 'error'
                            ? 'var(--err)'
                            : i.status === 'offline'
                              ? 'var(--warn)'
                              : 'var(--ink2)',
                      }}
                    >
                      {i.status === 'paused'
                        ? 'Pausado por você. Toque em Retomar.'
                        : statusText(i)}
                    </p>
                  ) : null}
                  <div
                    className="row wrap"
                    style={{ gap: 6 }}
                    role="radiogroup"
                    aria-label={`Tipo de ${i.file.name}`}
                  >
                    {CHIPS[video ? 'video' : 'photo'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        role="radio"
                        aria-checked={i.type === t}
                        className={i.type === t ? 'chip on' : 'chip'}
                        style={{ minHeight: 36, padding: '0 12px', fontSize: 13 }}
                        onClick={() => queue.setType(i.key, t)}
                      >
                        {MEDIA_TYPE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                  {i.status === 'paused' ? (
                    <button
                      type="button"
                      className="btn sm"
                      style={{ alignSelf: 'flex-start', minHeight: 44 }}
                      onClick={() => {
                        i.handle?.resume();
                        toast(`Retomando ${i.file.name}`);
                      }}
                    >
                      Retomar
                    </button>
                  ) : up ? (
                    <button
                      type="button"
                      className="btn sm g"
                      style={{ alignSelf: 'flex-start', minHeight: 44, padding: 0 }}
                      onClick={() => i.handle?.pause()}
                    >
                      Pausar
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn icon g"
                  aria-label={`Remover ${i.file.name}`}
                  onClick={() => setRemoving(i)}
                >
                  <Icon name="close" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <div className="empty" style={{ padding: '28px 8px' }}>
            <div className="emptyart" />
            <h2 style={{ fontSize: 18 }}>Nenhum arquivo ainda</h2>
            <p>
              {productId
                ? 'Tire uma foto ou escolha da galeria. Marque o tipo de cada uma na hora.'
                : 'Escolha o produto, depois tire a foto ou escolha da galeria.'}
            </p>
          </div>
        </div>
      )}

      <p className="xs muted">
        HEIC vira JPG. Vídeos ganham prévia comprimida; o original fica guardado.
      </p>
      <button
        type="button"
        className="btn p block"
        onClick={() => {
          if (!n) {
            toast('Adicione pelo menos uma foto ou vídeo');
            return;
          }
          setDone(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        Concluir
      </button>

      {removing ? (
        <Sheet label="Remover arquivo" onClose={() => setRemoving(null)}>
          <h2>Remover {removing.file.name}?</h2>
          <p className="sm muted">
            {removing.status === 'added'
              ? 'Já foi enviado; sai da mídia do produto também.'
              : 'O envio para e o arquivo sai da lista. O original continua no seu celular.'}
          </p>
          <button
            type="button"
            className="btn d block"
            onClick={() => {
              queue.remove(removing.key);
              toast(`${removing.file.name} removido`);
              setRemoving(null);
            }}
          >
            Remover
          </button>
          <button type="button" className="btn block" onClick={() => setRemoving(null)}>
            Cancelar
          </button>
        </Sheet>
      ) : null}
    </div>
  );
}
